<template>
  <b-modal id="create-deal" title="Create Follow Up Boss Deal" @shown="setupForm" @hidden="clearForm" hide-footer body-class="pt-0">
    <div v-if="type == null">
      <b-button block variant="primary" @click="type = 'find'">Find Deal</b-button>
      <b-button block variant="primary" @click="type = 'create'">Create New Deal</b-button>
    </div>
    <div v-if="type == 'find'">
      <b-button block variant="link" @click="type = null"><i class="fas fa-angle-double-left"></i> Back</b-button>
      <b-form-group label="Pipeline:" label-class="form-control-label" label-for="pipeline" class="mb-0" v-if="pipelines">
        <b-form-select v-model="searchPipelineId" @change="getDeals" required>
          <b-form-select-option :value="null">Filter by Pipeline</b-form-select-option>
          <b-form-select-option v-for="pipeline in pipelines" :key="pipeline.id" :value="pipeline.id">{{ pipeline.name }}</b-form-select-option>
        </b-form-select>
      </b-form-group>
      <b-list-group flush v-if="deals">
        <b-list-group-item class="d-flex justify-content-between align-items-center" v-for="deal in deals" :key="deal.id">
          <div>
            <h4 class="mb-0">{{ deal.name }}</h4>
            <p class="small">{{ deal.pipelineName }} <i class="fas fa-angle-double-right"></i> {{ deal.stageName }}</p>
            <p class="small mb-0">Price: {{ deal.price | currency }}</p>
            <p class="small mb-0">Close Date: {{ new Date(deal.projectedCloseDate) | date("MMM dd, Y") }}</p>

          </div>
          <b-button size="sm" variant="success" @click="connectDeal(deal.id)">Connect Deal</b-button>
        </b-list-group-item>
      </b-list-group>
    </div>
    <div v-if="type == 'create'">
      <b-button block variant="link" @click="type = null"><i class="fas fa-angle-double-left"></i> Back</b-button>
      <b-form @submit.prevent="submit">
        <b-form-group label="Name:" label-class="form-control-label" label-for="name">
          <b-form-input v-model="deal.name" required></b-form-input>
        </b-form-group>

        <b-row>
          <b-col md="6">
            <b-form-group label="Pipeline:" label-class="form-control-label" label-for="pipeline" v-if="pipelines">
              <b-form-select v-model="pipelineId" required>
                <b-form-select-option :value="null">Select a Pipeline</b-form-select-option>
                <b-form-select-option v-for="pipeline in pipelines" :key="pipeline.id" :value="pipeline.id">{{ pipeline.name }}</b-form-select-option>
              </b-form-select>
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Stage:" label-class="form-control-label" label-for="stageId" v-if="stages">
              <b-form-select v-model="deal.stageId" required>
                <b-form-select-option :value="null">Select a Stage</b-form-select-option>
                <b-form-select-option v-for="stage in stages" :key="stage.id" :value="stage.id">{{ stage.name }}</b-form-select-option>
              </b-form-select>
            </b-form-group>
          </b-col>
        </b-row>

        <b-row>
          <b-col md="6">
            <b-form-group label="Price:" label-class="form-control-label" label-for="price">
              <b-input-group prepend="$">
                <b-form-input type="number" v-model="deal.price" required></b-form-input>
              </b-input-group>
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Close Date:" label-class="form-control-label" label-for="projectedCloseDate">
              <b-form-datepicker id="close-date-datepicker" v-model="deal.projectedCloseDate" required></b-form-datepicker>
            </b-form-group>
          </b-col>
        </b-row>

        <b-row>
          <b-col md="6">
            <b-form-group label="Commission:" label-class="form-control-label" label-for="commission">
              <b-input-group prepend="$">
                <b-form-input type="number" step="0.01" v-model="deal.commissionValue"></b-form-input>
              </b-input-group>
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Team Commission:" label-class="form-control-label" label-for="teamCommission">
              <b-input-group prepend="$">
                <b-form-input type="number" step="0.01" v-model="deal.teamCommission"></b-form-input>
              </b-input-group>
            </b-form-group>
          </b-col>
        </b-row>

        <b-row>
          <b-col md="6">
            <label class="form-control-label" label-for="userIds">People:</label>
            <div class="mb-3">
              <b-avatar button badge-top badge-variant="danger" badge-offset="-0.25em" variant="primary" v-for="person in assignedPeople" :key="person.id" :text="person.initials" @click="removePerson(person)" :src="person.picture ? person.picture.small : null">
                <template #badge><b-icon icon="X"></b-icon></template>
              </b-avatar>
              <b-avatar id="peoplePopover" button variant="secondary" text="+" trigger="focus"></b-avatar>
              <b-popover title="Select People" target="peoplePopover" ref="peoplePopover" placement="rightbottom">
                <b-form-group>
                  <el-select filterable
                             v-model="person"
                             :remote="true"
                             :remote-method="getPeople"
                             placeholder="Search People"
                             name="People Search"
                             @change="addPerson">
                    <el-option v-for="person in people"
                               :key="person.id"
                               :label="person.name"
                               :value="person">
                    </el-option>
                  </el-select>
                </b-form-group>
              </b-popover>
            </div>
          </b-col>
          <b-col md="6">
            <label class="form-control-label" label-for="userIds">Team:</label>
            <div class="mb-3">
              <b-avatar button badge-top badge-variant="danger" badge-offset="-0.25em" variant="primary" v-for="user in assignedUsers" :key="user.id" :text="user.initials" @click="removeUser(user)" :src="user.picture ? user.picture.small : null">
                <template #badge><b-icon icon="X"></b-icon></template>
              </b-avatar>
              <b-avatar id="usersPopover" button variant="secondary" text="+" trigger="focus"></b-avatar>
              <b-popover title="Select Users" target="usersPopover" ref="usersPopover" placement="rightbottom">
                <b-form-group>
                  <el-select filterable
                             v-model="user"
                             placeholder="Search Team"
                             name="Team Search"
                             @change="addUser">
                    <el-option v-for="user in users"
                               :key="user.id"
                               :label="user.name"
                               :value="user">
                    </el-option>
                  </el-select>
                </b-form-group>
              </b-popover>
            </div>
          </b-col>
        </b-row>

        <b-form-group label="Description:" label-class="form-control-label" label-for="description">
          <b-form-textarea v-model="deal.description" placeholder="Add description" rows="3" max-rows="6" ></b-form-textarea>
        </b-form-group>

        <div class="text-right">
          <b-button pill type="submit" variant="success">SAVE</b-button>
        </div>
      </b-form>
    </div>
  </b-modal>
</template>

<script>
import axios from 'axios';
import swal from 'sweetalert2';
import 'sweetalert2/dist/sweetalert2.css';
import { Select, Option } from 'element-ui'
import errorsMixin from '@/mixins/errors-mixin';

export default {
  mixins: [errorsMixin],
  props: {
    transaction: null,
    userRoute: null,
    pipeline: null,
    commission: null,
    teamCommission: null
  },
  components: {
    [Select.name]: Select,
    [Option.name]: Option,
  },
  data() {
    return {
      type: null,
      people: [],
      users: [],
      pipelines: null,
      stages: null,
      name: '',
      deal: {
        name: '',
        stageId: null,
        description: '',
        peopleIds: [],
        userIds: [],
        price: 0,
        projectedCloseDate: new Date(),
        orderWeight: 0,
        commissionValue: 0,
        agentCommission: 0,
        teamCommission: 0,
      },
      listingTypes: ['coming-soon', 'active', 'cancelled', 'expired'],
      transactionTypes: ['pending', 'closed', 'failed'],
      deals: null,
      pipelineId: null,
      searchPipelineId: '',
      assignedPeople: [],
      assignedUsers: [],
      person: null,
      user: null
    }
  },
  methods: {
    async getPeople(name) {
      await axios.get(this.userRoute + "/integrations/fub/people?name=" + name)
      .then(response => {
        this.people = response.data;
      })
      .catch(error => {
        // error
      });
    },
    async getUsers(name) {
      await axios.get(this.userRoute + "/integrations/fub/users")
      .then(response => {
        this.users = response.data;
      })
      .catch(error => {
        // error
      });
    },
    async getStages() {
      let pipeline = this.pipelines.filter(pipeline => {
        return pipeline.id == this.pipelineId;
      })[0];

      this.stages = pipeline.stages;
    },
    async getPipelines() {
      await axios.get(this.userRoute + "/integrations/fub/pipelines")
      .then(response => {
        this.pipelines = response.data;
      })
      .catch(error => {
        // error
      });
    },
    async getDeals() {
      await axios.get(this.userRoute + "/integrations/fub/deals?pipelineId=" + this.searchPipelineId)
      .then(response => {
        this.deals = response.data;
      })
      .catch(error => {
        // error
      });
    },
    async connectDeal(dealId) {
      if (this.transaction) {
        await axios.post(this.userRoute + "/transactions/" + this.transaction.id + "/connect-deal", {
          deal_id: dealId
        })
        .then(response => {
          swal.fire({
            title: `Success`,
            text: response.data,
            showConfirmButton: false,
            timer: 1750,
            icon: 'success'
          }).then(result => {
            if (this.transaction) {
              this.$emit('getTransaction');
            } else {
              this.$emit('getPipeline');
            }
            
            this.$bvModal.hide('create-deal');
          });
        })
        .catch(error => {
          this.error(error);
        });
      } else {
        await axios.post(this.userRoute + "/pipelines/" + this.pipeline.id + "connect-deal", {
          deal_id: dealId
        })
        .then(response => {
          swal.fire({
            title: `Success`,
            text: response.data,
            showConfirmButton: false,
            timer: 1750,
            icon: 'success'
          }).then(result => {
            if (this.transaction) {
              this.$emit('getTransaction');
            } else {
              this.$emit('getPipeline');
            }
            
            this.$bvModal.hide('create-deal');
          });
        })
        .catch(error => {
          // error
        });
      }
    },
    mapTransaction() {
      this.deal.price = this.listingTypes.includes(this.transaction.status) ? this.transaction.listing_price : this.transaction.sale_price;
      this.deal.projectedCloseDate = this.listingTypes.includes(this.transaction.status) ? this.transaction.listing_date : this.transaction.close_date;
      this.deal.commissionValue = this.commission ? this.commission : 0;
      this.deal.teamCommission = this.teamCommission ? this.teamCommission : 0;
    },
    mapPipeline() {
      this.deal.price = this.pipeline.price;
    },
    async submit() {
      await axios.post(this.userRoute + "/integrations/fub/deals", {
        deal: this.deal,
        type: this.transaction ? 'transaction' : 'pipeline',
        type_id: this.transaction ? this.transaction.id : this.pipeline.id
      })
      .then(response => {
        swal.fire({
          title: `Success`,
          text: response.data,
          showConfirmButton: false,
          timer: 1750,
          icon: 'success'
        }).then(result => {
          if (this.transaction) {
            this.$router.go(-1);
          } else {
            this.$router.go(-1);
          }
          
          this.$bvModal.hide('create-deal');
        });
      })
      .catch(error => {
        this.error(error);
      });
    },
    setupForm() {
      this.getUsers();
      this.getPipelines();
      this.getDeals();

      if (this.transaction) {
        this.mapTransaction();
      } 

      if (this.pipeline) {
        this.mapPipeline();
      }
    },
    clearForm() {
      this.type = null;
      this.deal = {
        name: '',
        stageId: null,
        description: '',
        peopleIds: [],
        userIds: [],
        price: 0,
        projectedCloseDate: new Date(),
        orderWeight: 0,
        commissionValue: 0,
        agentCommission: 0,
        teamCommission: 0
      };
    },
    addPerson() {
      this.deal.peopleIds.push(this.person.id);
      let name = this.person.name.split(" ");
      this.assignedPeople.push({id: this.person.id, initials: Array.from(name[0])[0] + Array.from(name[1])[0], picture: this.person.picture});
      this.person = null;
      this.$refs.peoplePopover.$emit('close');
    },
    removePerson(person) {
      this.assignedPeople = this.assignedPeople.filter(assignedPerson => {
        return assignedPerson.id !== person.id;
      });

      this.deal.peopleIds = this.deal.peopleIds.splice(this.deal.peopleIds.indexOf(person.id), 1);
    },
    addUser() {
      this.deal.userIds.push(this.user.id);
      let name = this.user.name.split(" ");
      this.assignedUsers.push({id: this.user.id, initials: Array.from(name[0])[0] + Array.from(name[1])[0], picture: this.user.picture});
      this.user = null;
      this.$refs.usersPopover.$emit('close');
    },
    removeUser(user) {
      this.assignedUsers = this.assignedUsers.filter(assignedUser => {
        return assignedUser.id !== user.id;
      });

      this.deal.userIds = this.deal.userIds.splice(this.deal.userIds.indexOf(user.id), 1);
    }
  },
  watch: {
    transaction(to, from) {
      this.mapTransaction();
    },
    pipeline(to, from) {
      this.mapPipeline();
    },
    pipelineId(to, from) {
      this.getStages();
    }
  }
};
</script>